import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AuthService,
  CachedPropertiesService,
  ENVIRONMENT,
  LazyModuleInjector,
  ModalService,
  SharedService,
  UserService
} from '@cfp/core';
import { LoginCard, LoginLayout } from '@cfp/shared/ui/login';
import { TermsAndConditionsModal } from '@cfp/shared/ui/modals';
import { NewPasswordForm } from '@cfp/shared/ui/new-password-form';
import { Button, InputCheckbox, passwordHasMoreThanTwoSubsequentCharactersValidator } from '@cfp/ui';
import { TranslatedRouterLink } from '@cfp/ui/common';
import { passwordRepeatValidator, passwordValidator } from '@cfp/ui/core';
import { AlertService, InfoModal } from '@cfp/ui/feedback';
import { TranslateModule } from '@ngx-translate/core';
import {
  LoginPropertiesDTO,
  PasswordService,
  ResetPasswordCheckResponseDTO,
  ResetPasswordCommandDTO
} from 'csr-login-app';
import { forkJoin, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { LoginLoadingSpinner, LoginSso } from '../../components';
import { CUSTOM_PAGE_AFTER_ACCEPTING_TERMS_AND_CONDITIONS } from '../../tokens';

@Component({
  selector: 'cfp-password-reset-page',
  templateUrl: './password-reset-page.html',
  styleUrls: ['./password-reset-page.scss'],
  imports: [
    TranslateModule,
    TranslatedRouterLink,
    LoginLayout,
    LoginCard,
    LoginLoadingSpinner,
    ReactiveFormsModule,
    InputCheckbox,
    NewPasswordForm,
    Button,
    LoginSso
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PasswordResetPage implements OnInit {
  protected readonly isLoginLoading = signal(false);
  protected readonly isSubmitted = signal(false);
  protected readonly isTokenExpired = signal(false);
  protected readonly showAgreeTermsAndConditions = signal(false);
  protected readonly showAgreeTermsOfUse = signal(false);

  protected form?: FormGroup;

  protected readonly environment = inject(ENVIRONMENT);
  private readonly alertService = inject(AlertService);
  private readonly authService = inject(AuthService);
  private readonly sharedService = inject(SharedService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  private readonly modalService = inject(ModalService);
  private readonly userService = inject(UserService);
  private readonly passwordService = inject(PasswordService);
  private readonly cachedPropertiesService = inject(CachedPropertiesService);
  private readonly lazyModuleInjector = inject(LazyModuleInjector);

  private readonly pageAfterAcceptingTermsAndConditions: string | null = this.lazyModuleInjector.get(
    CUSTOM_PAGE_AFTER_ACCEPTING_TERMS_AND_CONDITIONS
  );

  private userName?: string;
  private token?: string;

  protected readonly loginProperties = signal<LoginPropertiesDTO | undefined>(undefined);

  protected readonly serverValidationErrors = signal<string[] | undefined>(undefined);
  private readonly passwordFormControlErrors = signal<ValidationErrors | undefined>(undefined);

  /**
   * First-login activation with a post-T&C page provided → Continue (then that page).
   */
  protected readonly submitButtonLabel = computed(() =>
    this.shouldContinueToPageAfterTerms() ? 'continue.button' : 'login.submit'
  );

  private shouldContinueToPageAfterTerms(): boolean {
    return !!this.pageAfterAcceptingTermsAndConditions && this.showAgreeTermsAndConditions();
  }

  public ngOnInit(): void {
    this.sharedService.emitDeleteLocalCache();

    this.token = this.activatedRoute.snapshot.queryParams['token'];
    this.userName = this.activatedRoute.snapshot.queryParams['user'];

    if (this.token == null || this.token.length === 0) {
      setTimeout(() => {
        // Added timeout, else alert service will not show the alert
        this.alertService.showAlert('error', 'login.error.general', 'login.error.passwordNoToken').subscribe(() => {
          this.goToLoginRoute();
        });
      });
    } else {
      this.isLoginLoading.set(true);
      forkJoin([
        this.passwordService.v1PasswordResetCheckTokenGet(this.token),
        this.cachedPropertiesService.v1PropertiesGet()
      ]).subscribe({
        next: ([response, loginProperties]: [ResetPasswordCheckResponseDTO, LoginPropertiesDTO]) => {
          this.buildForm(
            loginProperties.passwordLength,
            loginProperties.checkPasswordCompositionRules ?? false,
            response.disclaimerAccepted
          );
          this.loginProperties.set(loginProperties);
          this.isLoginLoading.set(false);
        },
        error: () => {
          this.isTokenExpired.set(true);
          this.isLoginLoading.set(false);
        }
      });
    }
  }

  /**
   * Checks if field has a minlength error
   */
  protected hasMinLengthError(field: string): boolean {
    const errors = this.form?.get(field)?.errors;
    return errors && errors.minlength && errors.minlength.actualLength < errors.minlength.requiredLength;
  }

  /**
   * Checks if valdiation should be displayed
   */
  protected showValidation(field: string, errorName: string, notErrorNames?: string[]): boolean {
    const errors = this.form?.get(field)?.errors;
    const formErrors = this.form?.errors;
    return (
      ((!!errors && errors[errorName] === true) || (!!formErrors && formErrors[errorName] === true)) &&
      (!notErrorNames ||
        !notErrorNames.some((notErrorName: string) => {
          return (!!formErrors && formErrors[notErrorName]) || (errors && errors[notErrorName]);
        }))
    );
  }

  protected send(): void {
    this.isSubmitted.set(true);
    if (!this.form || this.form.invalid || !this.token) {
      return;
    }

    this.isLoginLoading.set(true);

    const resetPasswordCommand: ResetPasswordCommandDTO = {
      environmentCode: this.environment.environmentCode.dto,
      token: this.token,
      password: this.form.value.password,
      repeatPassword: this.form.value.repeatPassword
    };

    const continueToPageAfterTerms = this.shouldContinueToPageAfterTerms();

    this.passwordService.v2PasswordResetPost(resetPasswordCommand).subscribe({
      next: () => {
        this.isLoginLoading.set(false);
        if (continueToPageAfterTerms) {
          this.login({ navigateToPageAfterTermsIfEligible: true });
          return;
        }

        this.alertService
          .showAlert(
            'success',
            'login.success.general',
            this.userName ? 'login.success.accountConfirmed' : 'login.success.passwordReset'
          )
          .subscribe(() => {
            this.login({ navigateToPageAfterTermsIfEligible: false });
          });
      },
      error: (error: ErrorEvent) => {
        this.isLoginLoading.set(false);

        if (Array.isArray(error.error)) {
          this.serverValidationErrors.set(error.error.map((error: any) => error.code));
        } else {
          this.serverValidationErrors.set(undefined);
          const errorMessageNewPassword: string = 'login.error.passwordNotReset';

          this.alertService
            .showAlert('error', 'login.error.general', errorMessageNewPassword)
            .subscribe(() => this.isSubmitted.set(false));
        }
      }
    });
  }

  private goToLoginRoute(): void {
    this.router.navigateByUrl(this.environment.loginRoute);
  }

  protected login(options: { navigateToPageAfterTermsIfEligible: boolean }): void {
    const password = this.form?.value.password;
    if (!this.userName || !password) {
      this.goToLoginRoute();
      return;
    }
    this.isLoginLoading.set(true);

    this.authService
      .login(this.userName, password)
      .pipe(
        mergeMap(() => {
          if (!(this.showAgreeTermsAndConditions() || this.showAgreeTermsOfUse())) {
            return of(null);
          }

          return this.userService.acceptDisclaimer();
        }),
        mergeMap(() => this.userService.get(false))
      )
      .subscribe({
        next: (user) => {
          this.isLoginLoading.set(false);
          const pageAfterTerms = this.pageAfterAcceptingTermsAndConditions;
          if (
            options.navigateToPageAfterTermsIfEligible &&
            pageAfterTerms &&
            user &&
            user.isARelationManager !== true
          ) {
            this.router.navigateByUrl(pageAfterTerms);
            return;
          }

          this.authService.goToApp();
        },
        error: () => this.router.navigateByUrl(this.environment.loginRoute)
      });
  }

  protected logout(): void {
    this.router.navigateByUrl('/login/logout');
  }

  protected showTermsAndConditions(): void {
    this.modalService.show(TermsAndConditionsModal, { class: 'modal-dialog-scrollable' });
  }

  protected showTermsOfUse(): void {
    this.modalService.show(InfoModal, {
      class: 'modal-dialog-scrollable',
      initialState: {
        title: 'termsOfUse',
        body: 'termsAndConditions'
      }
    });
  }

  private buildForm(passwordLength: number, checkPasswordCompositionRules: boolean, disclaimerAccepted: boolean): void {
    const passwordFormControl = new FormControl<string>('', {
      validators: [Validators.required, Validators.minLength(passwordLength)]
    });
    const validators = checkPasswordCompositionRules
      ? [passwordValidator]
      : [passwordHasMoreThanTwoSubsequentCharactersValidator];

    passwordFormControl.addValidators(validators);
    passwordFormControl.updateValueAndValidity();
    this.passwordFormControlErrors.set(passwordFormControl.errors ?? undefined);
    passwordFormControl.statusChanges?.subscribe(() => {
      this.passwordFormControlErrors.set(passwordFormControl.errors ?? undefined);
    });
    passwordFormControl.valueChanges?.subscribe(() => {
      this.serverValidationErrors.set(undefined);
    });

    const controlsConfig: {
      [key: string]: any;
    } = {
      password: passwordFormControl,
      repeatPassword: ['', [Validators.required]]
    };

    const showAgree = !disclaimerAccepted && !!this.userName && this.environment.modules.welcome;

    this.showAgreeTermsAndConditions.set(
      showAgree && this.environment.components.hidePasswordResetTermsOfConditions !== true
    );
    if (this.showAgreeTermsAndConditions()) {
      controlsConfig.agreeTermsAndConditions = [false, [Validators.requiredTrue]];
    }

    this.showAgreeTermsOfUse.set(showAgree && this.environment.components.hidePasswordResetTermsOfUse !== true);
    if (this.showAgreeTermsOfUse()) {
      controlsConfig.agreeTermsOfUse = [false, [Validators.requiredTrue]];
    }

    this.form = this.formBuilder.group(controlsConfig, {
      validators: passwordRepeatValidator('password', 'repeatPassword')
    });
  }
}
